// ** Redux Imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { createDedupedThunk } from '../../../../../../../../utility/createDedupedThunk'
import * as transactionsApi from '../api'

//Una llave por lectura: cada lista lleva su propio estado en listsMeta para que la petición
//de una no marque como cargando (ni descarte la respuesta de) otra
const LIST_KEYS = [
    'data',
    'dataMXNTraditional',
    'reportTransactions',
    'filterBankTransactions',
    'transactionsUnidentified',
    'transactionsUnidentifiedUSD',
    'transactionsIdentified',
    'transactionsIdentifiedUSD',
    'transactionsIntermediary',
    'alltransactionsIntermediary',
    'movements',
    'transactionsByMovement',
    'transactionsToConfirm',
    'transactionsToConfirmUSD',
    'transactionsSBC',
    'transactionsSBCByAccount',
    'transactionsToInvoice'
]

const initialListMeta = { status: 'idle', error: null, requestId: null }

const asList = (data) => (Array.isArray(data) ? data : [])
const isLatest = (meta, action) => meta?.requestId === action.meta.requestId

//Redux Toolkit rellena `action.error.message` con este literal cuando el thunk se rechaza
//con `rejectWithValue`, que es justo el caso de una caída de red (timeout, CORS, backend
//abajo): sin respuesta HTTP no hay `error.response.data` que pasarle
const RTK_REJECTED_SENTINEL = 'Rejected'

//`error` guarda lo que dijo el backend y nada más. Un fallo sin respuesta no dice nada, así
//que se guarda null en vez del literal en inglés de Redux Toolkit: quien lo muestre pondrá
//su propio texto. Que la petición falló ya lo dice `status`
const readError = (action) => {
    const message = action.error?.message

    return action.payload ?? (message === RTK_REJECTED_SENTINEL ? null : message) ?? null
}

/**
 * Crea un thunk de lectura sin caché: cada dispatch sale a la red.
 *
 * createDedupedThunk sólo corta un dispatch con los mismos argumentos mientras el primero
 * sigue en vuelo; una búsqueda con otros filtros siempre sale y la respuesta que llegue
 * tarde se descarta por requestId. Un dispatch cortado resuelve rechazado con
 * `meta.condition: true` y sin `payload`: quien lea el resultado del dispatch debe ignorarlo.
 */
const createReadThunk = (type, fetcher) => createDedupedThunk(type, async (arg, { rejectWithValue }) => {
    try {
        return {
            data: await fetcher(arg)
        }
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

/**
 * Registra pending/fulfilled/rejected de una lectura sobre `listsMeta[key]`.
 *
 * El requestId descarta las respuestas que llegan tarde (por ejemplo al cambiar de cuenta o
 * al filtrar dos veces seguidas), que si no sobreescribirían la tabla con datos de la
 * consulta anterior. `normalize` aplica a las listas que la pantalla recorre con map/filter.
 */
const addListCases = (builder, thunk, key, { normalize = false } = {}) => {
    builder.addCase(thunk.pending, (state, action) => {
        state.listsMeta[key] = { ...state.listsMeta[key], status: 'pending', error: null, requestId: action.meta.requestId }
    })
    builder.addCase(thunk.fulfilled, (state, action) => {
        if (!isLatest(state.listsMeta[key], action)) return
        state[key] = normalize ? asList(action.payload.data) : action.payload.data
        state.listsMeta[key] = { ...state.listsMeta[key], status: 'fulfilled' }
    })
    builder.addCase(thunk.rejected, (state, action) => {
        if (!isLatest(state.listsMeta[key], action)) return
        state.listsMeta[key] = {
            ...state.listsMeta[key],
            status: 'rejected',
            error: readError(action)
        }
    })
}

//Return all bank transactions Kuspoit and Traditional
export const getBankTransactions = createReadThunk('appBankTransactions/getBankTransactions', transactionsApi.fetchBankTransactions)

//Return transactions from Traditional MXN accounts
export const getBankTransactionsMXNTraditional = createReadThunk('appBankTransactions/getBankTransactionsMXNTraditional', transactionsApi.fetchBankTransactionsMXNTraditional)

export const getBankTransactionsByMovement = createReadThunk('appBankTransactions/getBankTransactionsByMovement', (data) => transactionsApi.fetchBankTransactionsByMovement(data.movement_type_id))

export const getBankTransactionsMovements = createReadThunk('appBankTransactionsMovements/getBankTransactionsMovements', () => transactionsApi.fetchMovementTypes())
//Never used
// // export const getAllBankTransactions = createAsyncThunk('appAllBankTransactions/getAllBankTransactions', async (data) => {
// //     const response = await axios.get(data.rangeDates !== undefined ? BANK_TRANSACTIONS_ENDPOINT.concat(`?end=${formattedDatePicker(data.rangeDates.end)} 23:${minutes + 1}:${seconds}.${miliseconds}&start=${formattedDateTimePicker(data.rangeDates.start)}`) : BANK_TRANSACTIONS_ENDPOINT)
// //     return {
// //         data: response.data
// //     }
// // })

export const getBankTransactionsToInvoice = createReadThunk('appBankTransactionsToInvoice/getBankTransactionsToInvoice', transactionsApi.fetchBankTransactionsToInvoice)

export const getReportTransactions = createReadThunk('appReportTransactions/getReportTransactions', transactionsApi.fetchReportTransactions)

//ReportTransactions elige el texto de su panel de error según el estado HTTP. Con rejectWithValue
//`action.error.message` queda en 'Rejected', así que el estado y el mensaje de axios viajan en `meta`
export const getFilterBankTransactions = createDedupedThunk('appFilterBankTransactions/getFilterBankTransactions', async (data, { rejectWithValue }) => {
    try {
        return {
            data: await transactionsApi.fetchFilterBankTransactions(data)
        }
    } catch (error) {
        return rejectWithValue(error.response?.data, { status: error.response?.status ?? null, errorMessage: error.message })
    }
})

export const getBankTransactionsIntermediary = createReadThunk('appBankTransactionsIntermediary/getBankTransactionsIntermediary', transactionsApi.fetchBankTransactionsIntermediary)

export const getAllBankTransactionsIntermediary = createReadThunk('appAllBankTransactionsIntermediary/getAllBankTransactionsIntermediary', transactionsApi.fetchAllBankTransactionsIntermediary)

export const getBankTransactionsUnidentified = createReadThunk('appBankTransactionsUnidentified/getBankTransactionsUnidentified', (rangeDates) => transactionsApi.fetchBankTransactionsUnidentified('MXN', rangeDates))
export const getBankTransactionsUnidentifiedUSD = createReadThunk('appBankTransactionsUnidentified/getBankTransactionsUnidentifiedUSD', (rangeDates) => transactionsApi.fetchBankTransactionsUnidentified('USD', rangeDates))

export const getBankTransactionsIdentified = createReadThunk('appBankTransactionsIdentified/getBankTransactionsIdentified', (rangeDates) => transactionsApi.fetchBankTransactionsIdentified('MXN', rangeDates))
export const getBankTransactionsIdentifiedUSD = createReadThunk('appBankTransactionsIdentified/getBankTransactionsIdentifiedUSD', (rangeDates) => transactionsApi.fetchBankTransactionsIdentified('USD', rangeDates))

//confirmChequeSBC recarga sin argumento: leer `data.rangeDates` lanzaba un TypeError y la recarga nunca salía
export const getBankTransactionsSBC = createReadThunk('appBankTransactionsSBC/getBankTransactionsSBC', (data) => transactionsApi.fetchBankTransactionsSBC(data?.rangeDates))

export const getBankTransactionsSBCByAccount = createReadThunk('appBankTransactionsSBCByAccount/getBankTransactionsSBCByAccount', transactionsApi.fetchBankTransactionsSBCByAccount)

export const getBankTransactionsToConfirm = createReadThunk('appBankTransactions/getBankTransactionsToConfirm', () => transactionsApi.fetchBankTransactionsToConfirm('MXN'))

export const getBankTransactionsToConfirmUSD = createReadThunk('appBankTransactions/getBankTransactionsToConfirmUSD', () => transactionsApi.fetchBankTransactionsToConfirm('USD'))

export const validateCheckSBC = createAsyncThunk('appBankTransactions/validateCheckSBC', async (data, { rejectWithValue }) => {
    try {
        return {
            data: await transactionsApi.validateCheckSBC(data.id)
        }
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const addBankTransactions = createAsyncThunk('appBankTransactions/addBankTransactions', async (bankTransaction, { rejectWithValue }) => {
    try {
        return await transactionsApi.createBankTransaction(bankTransaction)
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

//Sin recarga propia: la hace la pantalla con onTransactionsChanged, porque desde aquí no se conoce el rango
//buscado ni si la cuenta se consulta completa o sólo la de Bancos-Toluca
export const updateBankTransactions = createAsyncThunk('appBankTransactions/updateBankTransactions', async (bankTransaction, { rejectWithValue }) => {
    try {
        return {
            data: await transactionsApi.updateBankTransactionTimestamp(bankTransaction.id, bankTransaction)
        }
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const updateBankTransactionConcept = createAsyncThunk('appBankTransactions/updateBankTransactionConcept', async (bankTransaction, { rejectWithValue }) => {
    try {
        return {
            data: await transactionsApi.updateBankTransactionConcept(bankTransaction)
        }
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const identifyBankTransaction = createAsyncThunk('appBankTransactions/identifyBankTransaction', async (bankTransaction, { dispatch, rejectWithValue }) => {
    try {
        const data = await transactionsApi.identifyBankTransaction(bankTransaction.get("id"), bankTransaction)
        await dispatch(getBankTransactionsUnidentified())
        await dispatch(getBankTransactionsUnidentifiedUSD())
        await dispatch(getBankTransactionsIdentified())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

//Action type propio: compartía 'identifyBankTransaction' y no se distinguía de la identificación individual
export const identifyBankTransactions = createAsyncThunk('appBankTransactions/identifyBankTransactions', async (bankTransaction, { dispatch, rejectWithValue }) => {
    try {
        const data = await transactionsApi.identifyBankTransactions(bankTransaction)
        await dispatch(getBankTransactionsUnidentified())
        await dispatch(getBankTransactionsIdentified())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const changeBankTransaction = createAsyncThunk('appBankTransactions/changeBankTransaction', async (bankTransaction, { dispatch, rejectWithValue }) => {
    try {
        const data = await transactionsApi.changeBankTransaction(bankTransaction.get("id"), bankTransaction)
        await dispatch(getBankTransactionsUnidentified())
        await dispatch(getBankTransactionsIdentified())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const changeTransactionType = createAsyncThunk('appBankTransactions/changeTransactionType', async (bankTransaction, { dispatch, rejectWithValue }) => {
    try {
        const data = await transactionsApi.changeTransactionType(bankTransaction?.id, bankTransaction)
        await dispatch(getBankTransactionsUnidentified())
        await dispatch(getBankTransactionsIdentified())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const releaseTransactions = createAsyncThunk('appBankTransactions/releaseTransactions', async (data, { dispatch, rejectWithValue }) => {
    try {
        const response = await transactionsApi.releaseTransactions(data)
        await dispatch(getBankTransactionsUnidentified())
        await dispatch(getBankTransactionsUnidentifiedUSD())
        return response
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const confirmBankTransaction = createAsyncThunk('appBankTransactions/confirmBankTransaction', async (bankTransaction, { dispatch, rejectWithValue }) => {
    try {
        const data = await transactionsApi.confirmBankTransaction(bankTransaction)
        await dispatch(getBankTransactionsToConfirm())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const confirmDevolutionFondeo = createAsyncThunk('appBankTransactions/confirmDevolutionFondeo', async (bankTransaction, { rejectWithValue }) => {
    try {
        return await transactionsApi.confirmDevolutionFondeo(bankTransaction)
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const confirmChequeSBC = createAsyncThunk('appBankTransactions/confirmChequeSBC', async (bankTransaction, { dispatch, rejectWithValue }) => {
    try {
        const data = await transactionsApi.confirmChequeSBC(bankTransaction.id, bankTransaction.status_csb)
        await dispatch(getBankTransactionsSBC())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const cancelIdentifyBankTransaction = createAsyncThunk('appBankTransactions/cancelIdentifyBankTransaction', async (bankTransaction, { dispatch, rejectWithValue }) => {
    try {
        const data = await transactionsApi.cancelIdentifyBankTransaction(bankTransaction)
        await dispatch(getBankTransactionsIdentified())
        await dispatch(getBankTransactionsUnidentified())
        return data
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const deleteBankTransaction = createAsyncThunk('appBankTransactions/deleteBankTransaction', async (data, { rejectWithValue }) => {
    try {
        return await transactionsApi.deleteBankTransaction(data.transactionId)
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const linkCashCredit = createAsyncThunk('appBankTransactions/linkCashCredit', async (data, { rejectWithValue }) => {
    try {
        return {
            data: await transactionsApi.linkCashCredit(data.transaction_id, data.user_activity_id)
        }
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

//La lista ya respondió, con datos, vacía o con error. Todas arrancan en [], así que solo con el arreglo
//no se distingue "todavía no llega" de "llegó vacía"; quien la pinta lo usa para decidir si sigue el loader.
//`idle` cuenta como pendiente: hay pantallas que piden una lista hasta que termina otra
export const selectIsTransactionsListSettled = (state, key) => {
    const status = state.bankTransactions?.listsMeta?.[key]?.status
    return status === 'fulfilled' || status === 'rejected'
}

export const appBankTransactionsSlice = createSlice({
    name: 'appBankTransactions',
    initialState: {
        data: [],
        dataMXNTraditional: [],
        reportTransactions: [],
        // // allBankTransactions: [], --- IGNORE ---
        filterBankTransactions: [],
        transactionsUnidentified: [],
        transactionsUnidentifiedUSD: [],
        transactionsIdentified: [],
        transactionsIdentifiedUSD: [],
        movements: [],
        transactionsByMovement: [],
        transactionsToConfirm: [],
        transactionsToConfirmUSD: [],
        transactionsSBC: [],
        transactionsSBCByAccount: [],
        transactionsToInvoice: [],
        //Estado de cada lista: { [llave]: { status, error, requestId } }
        listsMeta: Object.fromEntries(LIST_KEYS.map(key => [key, { ...initialListMeta }]))
    },
    reducers: {},
    extraReducers: builder => {
        addListCases(builder, getBankTransactions, 'data', { normalize: true })
        addListCases(builder, getBankTransactionsMXNTraditional, 'dataMXNTraditional', { normalize: true })
        addListCases(builder, getReportTransactions, 'reportTransactions')
        //Never used
        // // builder.addCase(getAllBankTransactions.fulfilled, (state, action) => {
        // //     state.allBankTransactions = action.payload.data
        // // })
        addListCases(builder, getBankTransactionsUnidentified, 'transactionsUnidentified', { normalize: true })
        addListCases(builder, getBankTransactionsIdentified, 'transactionsIdentified', { normalize: true })
        addListCases(builder, getBankTransactionsUnidentifiedUSD, 'transactionsUnidentifiedUSD', { normalize: true })
        addListCases(builder, getBankTransactionsIdentifiedUSD, 'transactionsIdentifiedUSD', { normalize: true })
        addListCases(builder, getBankTransactionsIntermediary, 'transactionsIntermediary')
        addListCases(builder, getAllBankTransactionsIntermediary, 'alltransactionsIntermediary', { normalize: true })
        addListCases(builder, getBankTransactionsMovements, 'movements', { normalize: true })
        addListCases(builder, getFilterBankTransactions, 'filterBankTransactions')
        addListCases(builder, getBankTransactionsByMovement, 'transactionsByMovement', { normalize: true })
        addListCases(builder, getBankTransactionsToConfirm, 'transactionsToConfirm', { normalize: true })
        addListCases(builder, getBankTransactionsToConfirmUSD, 'transactionsToConfirmUSD', { normalize: true })
        addListCases(builder, getBankTransactionsSBC, 'transactionsSBC', { normalize: true })
        addListCases(builder, getBankTransactionsSBCByAccount, 'transactionsSBCByAccount', { normalize: true })
        addListCases(builder, getBankTransactionsToInvoice, 'transactionsToInvoice')
    }
}
)

export default appBankTransactionsSlice.reducer
